import { Spacing } from "../../layout";
import { HorizontalAlignment, ResponsiveAlignment, VerticalAlignment } from "./ShelfAlignment";

/** Legacy interface kept for backward compatibility - use conditional types instead. */
export interface ResponsiveAlignmentConfig {
  readonly default?: VerticalAlignment;
  readonly wrap?: HorizontalAlignment;
  readonly stack?: HorizontalAlignment;
}

/** Type-safe preset configurations for common responsive patterns */
export type ResponsivePreset = {
  readonly responsive?: boolean | string;
  readonly spacing?: Spacing;
  readonly adaptiveSpacing?: boolean;
  readonly responsiveAlignment?: ResponsiveAlignment | ResponsiveAlignmentConfig;
};

/** Pre-configured responsive presets for common layout patterns */
export const ResponsivePresets: Record<string, ResponsivePreset> = {
  /** Mobile-first layout that stacks on narrow screens with left alignment */
  mobileFirst: {
    responsive: true,
    responsiveAlignment: {
      default: "top", // Items align to top when horizontal
      stack: "left", // Items align to left when stacked vertically
    },
  },

  /** Center-focused layout that maintains center alignment across all responsive states */
  centerFocused: {
    responsive: true,
    responsiveAlignment: {
      default: "center", // Items align to center vertically when horizontal
      stack: "center", // Items align to center when stacked vertically
      wrap: "center", // Wrapped rows align to center
    },
  },

  /** Flexible layout that adapts alignment based on available space and wrapping behavior */
  adaptive: {
    responsive: "wrap",
    responsiveAlignment: {
      default: "center", // Items align to center vertically when horizontal
      stack: "center", // Items align to center when stacked
      wrap: "left", // Wrapped rows align to left (most natural for wrapped content)
    },
  },

  /** Compact layout optimized for dense information displays with tight spacing */
  compact: {
    responsive: "wrap",
    spacing: "small",
    responsiveAlignment: {
      default: "top", // Items align to top when horizontal (compact)
      stack: "left", // Items align to left when stacked (compact)
      wrap: "left", // Wrapped rows align to left (most compact)
    },
  },
};

/**
 * Utility function to merge a responsive preset with custom props.
 * Custom props will override preset values.
 *
 * @example
 * const props = withResponsivePreset(ResponsivePresets.adaptive, {
 *   spacing: 'small',
 *   responsiveAlignment: { wrap: 'center' }
 * });
 */
export function withResponsivePreset(preset: ResponsivePreset, overrides: Partial<ResponsivePreset> = {}): ResponsivePreset {
  // Handle responsiveAlignment merging properly for both string and object types
  let mergedAlignment: ResponsiveAlignment | ResponsiveAlignmentConfig | undefined;

  const presetAlignment = preset.responsiveAlignment;
  const overrideAlignment = overrides.responsiveAlignment;

  if (overrideAlignment && presetAlignment) {
    // Both exist - merge intelligently
    if (typeof overrideAlignment === "string") {
      // Override is string, use it directly
      mergedAlignment = overrideAlignment;
    } else if (typeof presetAlignment === "string") {
      // Preset is string, override is object, use override
      mergedAlignment = overrideAlignment;
    } else {
      // Both are objects, merge them
      mergedAlignment = {
        ...presetAlignment,
        ...overrideAlignment,
      };
    }
  } else {
    // Use whichever one exists
    mergedAlignment = overrideAlignment || presetAlignment;
  }

  return {
    ...preset,
    ...overrides,
    responsiveAlignment: mergedAlignment,
  };
}
